import { useState, useCallback, useRef, useEffect } from 'react';
import { Position, Size } from './types';
import ModalManager from './ModalManager';

export interface UseDraggableModalConfig {
  initialPosition?: Position;
  initialSize?: Size;
  initialOpen?: boolean;
  initialMinimized?: boolean;
  id?: string;
  onOpen?: () => void;
  onClose?: () => void;
  onMinimize?: (isMinimized: boolean) => void;
  onResize?: (size: Size) => void;
  onDrag?: (position: Position) => void;
}

export interface UseDraggableModalReturn {
  // State
  isOpen: boolean;
  position: Position;
  size: Size;
  isMinimized: boolean;
  zIndex: number;
  modalId: string;
  
  // Actions
  openModal: (customPosition?: Position, customSize?: Size) => void;
  closeModal: () => void;
  toggleModal: () => void;
  minimizeModal: () => void;
  maximizeModal: () => void;
  toggleMinimize: () => void;
  updatePosition: (newPosition: Position) => void;
  updateSize: (newSize: Size) => void;
  bringToFront: () => void;
  resetToDefaults: () => void;
  
  // Utilities
  isActive: boolean;
}

/**
 * Hook for managing draggable modal state
 * Provides state management for position, size, visibility, and z-index
 */
export const useDraggableModal = (config: UseDraggableModalConfig = {}): UseDraggableModalReturn => {
  const {
    initialPosition = { x: 100, y: 100 },
    initialSize = { width: 800, height: 600 },
    initialOpen = false,
    initialMinimized = false,
    id,
    onOpen,
    onClose,
    onMinimize,
    onResize,
    onDrag
  } = config;

  // Generate unique ID if not provided
  const modalId = useRef(id || `modal-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`).current;
  
  const [isOpen, setIsOpen] = useState(initialOpen);
  const [position, setPosition] = useState<Position>(initialPosition);
  const [size, setSize] = useState<Size>(initialSize);
  const [isMinimized, setIsMinimized] = useState(initialMinimized);
  const [zIndex, setZIndex] = useState(10000);

  const modalManager = ModalManager.getInstance();

  // Register/unregister modal with manager
  useEffect(() => {
    if (isOpen) {
      const newZIndex = modalManager.registerModal(modalId);
      setZIndex(newZIndex);
      onOpen?.();
    } else {
      modalManager.unregisterModal(modalId);
      onClose?.();
    }

    return () => {
      if (isOpen) {
        modalManager.unregisterModal(modalId);
      }
    };
  }, [isOpen, modalId, modalManager, onOpen, onClose]);

  const openModal = useCallback((customPosition?: Position, customSize?: Size) => {
    if (customPosition) setPosition(customPosition);
    if (customSize) setSize(customSize);
    setIsOpen(true);
  }, []);

  const closeModal = useCallback(() => {
    setIsOpen(false);
  }, []);

  const toggleModal = useCallback(() => {
    setIsOpen(prev => !prev);
  }, []);

  const minimizeModal = useCallback(() => {
    setIsMinimized(true);
    onMinimize?.(true);
  }, [onMinimize]);

  const maximizeModal = useCallback(() => {
    setIsMinimized(false);
    onMinimize?.(false);
  }, [onMinimize]);

  const toggleMinimize = useCallback(() => {
    const newMinimized = !isMinimized;
    setIsMinimized(newMinimized);
    onMinimize?.(newMinimized);
  }, [isMinimized, onMinimize]);

  const updatePosition = useCallback((newPosition: Position) => {
    setPosition(newPosition);
    onDrag?.(newPosition);
  }, [onDrag]);

  const updateSize = useCallback((newSize: Size) => {
    setSize(newSize);
    onResize?.(newSize);
  }, [onResize]);

  const bringToFront = useCallback(() => {
    if (isOpen) {
      const newZIndex = modalManager.bringToFront(modalId);
      setZIndex(newZIndex);
    }
  }, [isOpen, modalId, modalManager]);

  const resetToDefaults = useCallback(() => {
    setPosition(initialPosition);
    setSize(initialSize);
    setIsMinimized(initialMinimized);
  }, [initialPosition, initialSize, initialMinimized]);

  const isActive = isOpen && modalManager.isActive(modalId);

  return {
    // State
    isOpen,
    position,
    size,
    isMinimized,
    zIndex,
    modalId,
    
    // Actions
    openModal,
    closeModal,
    toggleModal,
    minimizeModal,
    maximizeModal,
    toggleMinimize,
    updatePosition,
    updateSize,
    bringToFront,
    resetToDefaults,
    
    // Utilities
    isActive
  };
};
